import { Fragment, createRef } from "react";
import classNames from "classnames";
import { isFunction } from "lodash";
import { MessageIndicatorLines } from "@/react/components/Chat/ChatWindowV2/MessageItem/MessageIndicatorLines";
import { CursorPagination } from "@/react/components/shared/Pagination/CursorPagination";
import { usePunditUserContext } from "@circle-react/contexts";
import { formattedMessageSentAt } from "@circle-react/helpers/dateTimeHelpers/formattedMessageSentAt";
import type { Message } from "@circle-react/types/CommunityInbox/Message";
import { MESSAGE_CONTAINER_ID } from "../../hooks/useScroll";
import { MessageBox } from "../MessageBox";
import { AIPausedIndicator } from "./AIPausedIndicator";
import { BroadcastModeIndicator } from "./BroadcastModeIndicator";
import { ConversationLengthWarning } from "./ConversationLengthWarning";
import { MessageContentItem } from "./MessageContentItem";
import { NewConversationBlock } from "./NewConversationBlock";
import { PreviewConversationBlock } from "./PreviewConversationBlock";
import { PreviewPausedIndicator } from "./PreviewPausedIndicator";
import {
  isAIPausedIndicatorSet,
  isAIResumedIndicatorset,
  isIndicatorNeverSet,
} from "./helper";

interface ChatWindowProps {
  agentName?: string;
  chatCommunityMember?: any;
  createMessage: (data: any) => void;
  currentChat: any;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isDisabled?: boolean;
  isDms: boolean;
  isLoading: boolean;
  isNewConversation: boolean;
  isPreviewMode?: boolean;
  isProcessing?: boolean;
  messages: Message[];
  onRestart?: () => void;
}

export const ChatWindow = ({
  messages,
  createMessage,
  isDms,
  fetchNextPage,
  hasNextPage,
  isLoading,
  isNewConversation,
  isPreviewMode,
  agentName,
  isProcessing,
  isDisabled,
  chatCommunityMember,
  currentChat,
  onRestart,
}: ChatWindowProps) => {
  const prevDayRef: any = createRef<string | null>();
  const shouldDisplayPreviewBlock = isPreviewMode && !messages?.length;
  let aiIndicator: {
    id: number | null;
    isAiSnoozed: boolean | null;
  } = {
    id: null,
    isAiSnoozed: null,
  };

  const { currentCommunityMember } = usePunditUserContext();
  const isCurrentUserCommunityMember =
    currentCommunityMember?.id === chatCommunityMember?.id;
  const conversationStatus = currentChat?.conversation_status || "normal";

  const arePauseKeywordsDetectedInPreview =
    isPreviewMode &&
    messages.some(message => message.pause_keywords_detected_in_preview);

  const shouldDisplayMessageBox = isDms || !isCurrentUserCommunityMember;
  const shouldDisplayConversationLengthWarning =
    !isPreviewMode && conversationStatus !== "normal";
  let shouldDisplayMessageInput;
  if (isPreviewMode) {
    shouldDisplayMessageInput = !arePauseKeywordsDetectedInPreview;
  } else {
    shouldDisplayMessageInput =
      !isDms ||
      conversationStatus === "normal" ||
      conversationStatus === "getting_too_long";
  }

  return (
    <div
      className={classNames(
        "bg-primary flex w-full flex-col justify-between",
        isNewConversation || isPreviewMode
          ? "h-full"
          : "lg:h-[calc(100%-64px)]",
        isDms ? "h-[calc(100dvh-64px)]" : "h-[calc(100dvh-120px)]",
      )}
    >
      {hasNextPage && (
        <CursorPagination
          componentId={MESSAGE_CONTAINER_ID}
          fetchNewPage={fetchNextPage}
          onEvent="scrollUp"
          messageId={null}
          isLoading={isLoading}
        />
      )}
      <div id={MESSAGE_CONTAINER_ID} className="overflow-auto px-2">
        {messages.map((message: Message) => {
          const {
            id,
            ai_snoozed: isAiSnoozed,
            created_at: createdAt,
            pause_keywords_detected_in_preview:
              isPauseKeywordsDetectedInPreview,
          } = message;

          const { day } = formattedMessageSentAt(createdAt);
          const isDayChange =
            !isFunction(prevDayRef) && prevDayRef?.current !== day; //null
          if (prevDayRef && !isFunction(prevDayRef)) {
            prevDayRef.current = day;
          }

          const shouldShowPauseIndicator = isPreviewMode
            ? Boolean(isPauseKeywordsDetectedInPreview)
            : isAiSnoozed;

          if (
            (isIndicatorNeverSet(aiIndicator) ||
              isAIResumedIndicatorset(aiIndicator)) &&
            shouldShowPauseIndicator
          ) {
            aiIndicator = {
              id,
              isAiSnoozed: shouldShowPauseIndicator,
            };
          } else if (
            isAIPausedIndicatorSet(aiIndicator) &&
            !shouldShowPauseIndicator
          ) {
            aiIndicator = {
              id,
              isAiSnoozed: false,
            };
          }

          return (
            <Fragment key={id}>
              {!isPreviewMode && (
                <MessageIndicatorLines
                  showDateChangeLine={isDayChange}
                  day={day}
                />
              )}
              <MessageContentItem message={message} />
              {aiIndicator.id === id && <AIPausedIndicator aiPaused />}
            </Fragment>
          );
        })}
        {!isPreviewMode &&
          !isAIPausedIndicatorSet(aiIndicator) &&
          currentChat?.ai_snoozed && (
            <AIPausedIndicator aiPaused={currentChat?.ai_snoozed} />
          )}
        {!isPreviewMode &&
          isAIPausedIndicatorSet(aiIndicator) &&
          !currentChat?.ai_snoozed && (
            <AIPausedIndicator aiPaused={currentChat?.ai_snoozed} />
          )}
        {isPreviewMode &&
          !isAIPausedIndicatorSet(aiIndicator) &&
          arePauseKeywordsDetectedInPreview && <AIPausedIndicator aiPaused />}
        {isPreviewMode && arePauseKeywordsDetectedInPreview && onRestart && (
          <PreviewPausedIndicator onRestart={onRestart} />
        )}
      </div>
      {shouldDisplayMessageBox && (
        <div className={classNames("px-4", isPreviewMode ? "py-2" : "pt-2")}>
          {isNewConversation && (
            <NewConversationBlock createMessage={createMessage} />
          )}
          {shouldDisplayPreviewBlock && (
            <PreviewConversationBlock createMessage={createMessage} />
          )}
          {isDisabled && <BroadcastModeIndicator agentName={agentName || ""} />}
          {!isDisabled && (
            <>
              {shouldDisplayConversationLengthWarning && (
                <ConversationLengthWarning
                  communityBotProfile={currentChat?.community_bot_profile}
                  conversationStatus={conversationStatus}
                  isDms={isDms}
                />
              )}
              {shouldDisplayMessageInput && (
                <MessageBox
                  onSubmit={createMessage}
                  isProcessing={isProcessing}
                  agentName={isDms ? agentName : chatCommunityMember?.name}
                  isDms={isDms}
                  isPreviewMode={isPreviewMode}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
